import clsx from "clsx";
import React, { useState } from "react";
import { Button } from "../catalyst/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../catalyst/dialog";
import { Radio, RadioField, RadioGroup } from "../catalyst/radio";
import { Description, Label } from "../catalyst/fieldset";
import Icon from "../ui/Icon";

interface DataDeletionConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (deleteData: boolean) => void;
  title: string;
  description?: string;
  institutionName?: string;
  /** 데이터 유지 옵션 설명 */
  keepDataDescription?: string;
  /** 데이터 삭제 옵션 설명 */
  deleteDataDescription?: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
}

const DataDeletionConfirmModal: React.FC<DataDeletionConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  institutionName,
  keepDataDescription = "전송요구서만 철회하고, 기존 채팅 이력과 의료 정보는 유지합니다.",
  deleteDataDescription = "전송요구서와 함께 채팅 이력, 수집된 의료 정보를 모두 삭제합니다.",
  confirmText = "확인",
  cancelText = "취소",
  isLoading = false,
}) => {
  const [deleteData, setDeleteData] = useState(false);

  const handleConfirm = () => {
    onConfirm(deleteData);
  };

  const handleClose = () => {
    if (!isLoading) {
      setDeleteData(false);
      onClose();
    }
  };

  // description이 없으면 institutionName으로 기본 메시지 생성
  const displayDescription =
    description ||
    (institutionName ? `${institutionName}와의 연결을 해제하시겠습니까?` : null);

  return (
    <Dialog open={isOpen} onClose={handleClose} size="md">
      <DialogBody>
        {/* 아이콘 */}
        <div className="w-16 h-16 mx-auto bg-yellow-100 rounded-full flex items-center justify-center mb-4">
          <Icon name="x" size="xl" className="text-yellow-600" />
        </div>

        <DialogTitle className="text-center">{title}</DialogTitle>

        {displayDescription && (
          <p className="text-text-secondary text-center mt-2 whitespace-pre-line">
            {displayDescription}
          </p>
        )}

        {/* 데이터 삭제 옵션 */}
        <div className="mt-6">
          <p className="text-s font-medium text-text-primary mb-3">
            데이터 처리 방식을 선택해주세요
          </p>

          <RadioGroup value={deleteData ? "delete" : "keep"} onChange={(value) => setDeleteData(value === "delete")}>
            <div className="space-y-3">
              {/* 데이터 유지 옵션 */}
              <div
                className={clsx(
                  "p-4 border rounded-m cursor-pointer transition-colors",
                  !deleteData
                    ? "border-blue-500 bg-blue-50"
                    : "border-border-default hover:border-border-strong"
                )}
              >
                <RadioField>
                  <Radio value="keep" color="blue" />
                  <Label className="cursor-pointer">데이터 유지</Label>
                  <Description>{keepDataDescription}</Description>
                </RadioField>
              </div>

              {/* 데이터 삭제 옵션 */}
              <div
                className={clsx(
                  "p-4 border rounded-m cursor-pointer transition-colors",
                  deleteData
                    ? "border-red-500 bg-red-50"
                    : "border-border-default hover:border-border-strong"
                )}
              >
                <RadioField>
                  <Radio value="delete" color="red" />
                  <Label className="cursor-pointer">데이터 삭제</Label>
                  <Description>{deleteDataDescription}</Description>
                </RadioField>
              </div>
            </div>
          </RadioGroup>
        </div>
      </DialogBody>

      <DialogActions>
        <Button outline onClick={handleClose} disabled={isLoading}>
          {cancelText}
        </Button>
        <Button
          color={deleteData ? "red" : "blue"}
          onClick={handleConfirm}
          disabled={isLoading}
        >
          {isLoading ? "처리 중..." : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DataDeletionConfirmModal;
